import {
  ReactFlow,
  Background,
  Connection,
  addEdge,
  MiniMap,
  Edge,
  Node,
  MarkerType,
  applyEdgeChanges,
  applyNodeChanges
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useCallback, useState } from "react";
import { nanoid } from "nanoid";

import CustomNode from "./custom-node";
import CustomEdgeComponent from "./custom-edge";

interface Props {
  nodes: Node[];
  edges: Edge[];
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
}

const Workflow: React.FC<Props> = ({ nodes, edges, setNodes, setEdges }) => {
  const [selectedConnection, setSelectedConnection] = useState<Connection | null>(null);

  const handleConnect = useCallback(
    (params: Connection) => {
      // Get the source node to check its type
      const sourceNode = nodes.find(n => n.id === params.source);
      
      // For condition nodes (type === 3), we need to prompt for true/false
      if (sourceNode?.data?.type === 3) {
        setSelectedConnection(params);
      } else {
        // Ensure we don't keep a stale condition prompt open.
        setSelectedConnection(null);
        // For non-condition nodes, create edge immediately with no condition type
        createEdge(params, 0);
      }
    },
    [nodes, setEdges]
  );

  const createEdge = (params: Connection, conditionType: number) => {
    const label = conditionType === 1 ? "true" : conditionType === 2 ? "false" : undefined;
    setEdges((eds) =>
      addEdge(
        {
          ...params,
          id: nanoid(),
          type: "bezier",
          animated: true,
          // These top-level fields are what our backend DTO binds to.
          label,
          conditionType,
          data: {
            label,
            conditionType
          },
          markerEnd: {
            type: MarkerType.Arrow,
            width: 20,
            height: 20,
            color: "#0099A5"
          },
          style: {
            strokeWidth: 2,
            stroke: "#0099A5"
          }
        } as any,
        eds
      )
    );
  };

  const handleNodesChange = useCallback(
    (changes: any) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [setNodes]
  );

  const handleEdgesChange = useCallback(
    (changes: any) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [setEdges]
  );

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={handleNodesChange}
      onEdgesChange={handleEdgesChange}
      onConnect={handleConnect}
      snapToGrid
      snapGrid={[10, 10]}
      nodeTypes={{ custom: CustomNode }}
      edgeTypes={{ custom: CustomEdgeComponent }}
    >
      <Background />
      <MiniMap
        pannable
        nodeColor="#0099A5"
        position="top-right"
        maskColor="#0099A520"
      />
      
      {selectedConnection && (
        <div className="absolute left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-4 shadow-lg">
          <p className="mb-4 font-semibold">Select condition path:</p>
          <div className="flex gap-2">
            <button
              onClick={() => {
                createEdge(selectedConnection, 1);
                setSelectedConnection(null);
              }}
              className="rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600"
            >
              True
            </button>
            <button
              onClick={() => {
                createEdge(selectedConnection, 2);
                setSelectedConnection(null);
              }}
              className="rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
            >
              False
            </button>
            <button
              onClick={() => setSelectedConnection(null)}
              className="rounded border border-gray-300 px-4 py-2 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </ReactFlow>
  );
};

export default Workflow;
